/*
  Custom playback rate button for VideoJS with popover controls
  Replaces the default VideoJS playback rate menu with a custom popover interface
  featuring a speed slider and preset buttons
  Call `player.customPlaybackRateButton()` to initialize
*/

import videojs from 'video.js'
import './playback_rate_button.scss'

const Button = videojs.getComponent('Button')

const PRESET_RATES = [0.5, 0.7, 1.0, 1.2, 1.5, 1.7, 2.0, 2.5]

class CustomPlaybackRateButton extends Button {
  constructor(player, options) {
    super(player, options)

    this.popoverContentEl = this.generatePopoverContentEl()
    this.updatePopoverContentEl()
    this.attachEventListenersToPopoverContentEl()
    this.makePopover()

    // Listen for playback rate changes from other sources
    this.player().on('ratechange', () => {
      this.updateButtonText()
      this.updatePopoverContentEl()
    })

    this.updateButtonText()
  }

  buildCSSClass() {
    return `vjs-custom-playback-rate-button ${super.buildCSSClass()}`
  }

  updateButtonText() {
    const rateText = `${this.currentRate}x`
    this.el().innerHTML = `<span class="vjs-icon-placeholder" aria-hidden="true"></span><span class="vjs-control-text">Playback Rate</span><span class="rate-text">${rateText}</span>`
  }

  makePopover() {
    const el = this.el()

    el.popoverContentEl = this.popoverContentEl
    el.setAttribute('data-trigger', 'click')
    el.setAttribute('data-placement', 'top')
    el.setAttribute('data-theme', 'light')
    el.setAttribute('data-interactive', 'true')
    el.setAttribute('data-arrow', false)
    el.setAttribute('data-tooltip-class', 'popover-controller--no-padding')
    el.setAttribute('data-controller', 'popover')
    el.setAttribute('data-max-width', '99vw')

    return el
  }

  // Override to not prevent default/stop propogation so stimulus can handle it...
  handleClick() {}
  emitTapEvents() {}

  generatePopoverContentEl() {
    const el = document.createElement('div')
    el.classList.add('tw')
    el.innerHTML = `
      <div class="tw">
        <div class="select-none px-3 py-4 flex flex-col gap-3 w-full">
          <div class="flex justify-between items-center text-sm font-semibold text-fg-base">
            <h3>Speed</h3>
            <span class="js-speed"></span>
          </div>

          <!-- Speed Slider -->
          <div>
            <div class="flex items-center space-x-3">
              <button
                class="js-decrease-speed flex-shrink-0 w-8 h-8 rounded-full border border-border-base flex items-center justify-center hover:opacity-50 focus:outline-none"
              >
                <span class="text-fg-muted text-lg font-medium">−</span>
              </button>

              <div class="flex-1 relative flex">
                <input
                  type="range"
                  min="0.5"
                  max="2.0"
                  step="0.1"
                  value=""
                  class="js-slider no-reset w-full bg-bg-muted rounded-lg cursor-pointer vjs-playback-rate-slider"
                />
              </div>

              <button
                class="js-increase-speed flex-shrink-0 w-8 h-8 rounded-full border border-border-base flex items-center justify-center hover:opacity-75 disabled:cursor-disabled disabled:opacity-50 focus:outline-none"
              >
                <span class="text-fg-muted text-lg font-medium">+</span>
              </button>
            </div>
          </div>

          <!-- Preset Buttons -->
          <div class="flex justify-between gap-1.5">
            ${PRESET_RATES.map(
              (rate) => `
              <button
                data-rate="${rate}"
                class="px-2 py-1 text-xs font-semibold rounded bg-transparent border border-border-base is-active:border-primary-500 is-active:bg-primary-500 is-active:text-primary-contrast"
              >
                ${rate.toFixed(1)}
              </button>
            `
            ).join('')}
          </div>
        </div>
      </div>
    `
    return el
  }

  attachEventListenersToPopoverContentEl() {
    this.popoverContentEl
      .querySelector('.js-increase-speed')
      .addEventListener('click', this.increaseSpeed)
    this.popoverContentEl
      .querySelector('.js-decrease-speed')
      .addEventListener('click', this.decreaseSpeed)
    this.popoverContentEl
      .querySelector('.js-slider')
      .addEventListener('input', this.onSliderInput)
    this.popoverContentEl.addEventListener('click', (e) => {
      const rateButton = e.target.closest('[data-rate]')
      if (!rateButton) return
      this.setSpeed(rateButton.dataset.rate)
    })
  }

  updatePopoverContentEl() {
    const currentRate = this.currentRate
    const currentRateFormatted = currentRate.toFixed(2)
    this.popoverContentEl.querySelector(
      '.js-speed'
    ).innerText = `${currentRateFormatted}x`
    this.popoverContentEl.querySelector('.js-decrease-speed').disabled =
      currentRate <= PRESET_RATES[0]
    this.popoverContentEl.querySelector('.js-increase-speed').disabled =
      currentRate >= PRESET_RATES[PRESET_RATES.length - 1]
    this.popoverContentEl.querySelector('.js-slider').value = currentRate
    this.popoverContentEl
      .querySelector('.is-active[data-rate]')
      ?.classList?.remove('is-active')
    this.popoverContentEl
      .querySelector(`[data-rate="${currentRate}"]`)
      ?.classList?.add('is-active')
  }

  setSpeed = (rate) => {
    this.player().playbackRate(rate)
  }

  adjustSpeed = (delta) => {
    const newRate = Math.max(0.5, Math.min(2.0, this.currentRate + delta))
    const roundedRate = Math.round(newRate * 10) / 10 // Round to 1 decimal place
    this.setSpeed(roundedRate)
  }

  increaseSpeed = () => {
    this.adjustSpeed(0.1)
  }

  decreaseSpeed = () => {
    this.adjustSpeed(-0.1)
  }

  onSliderInput = (e) => {
    this.setSpeed(e.target.value)
  }

  get currentRate() {
    return this.player().playbackRate() || 1
  }
}

CustomPlaybackRateButton.prototype.controlText_ = 'Playback Rate'
videojs.registerComponent('CustomPlaybackRateButton', CustomPlaybackRateButton)

videojs.registerPlugin('customPlaybackRateButton', function () {
  const player = this

  player.ready(() => {
    const controlBar = player.controlBar
    const playbackRatesButton = controlBar.getChild('PlaybackRateMenuButton')

    if (playbackRatesButton) {
      const index = controlBar.children().indexOf(playbackRatesButton)
      player.controlBar.removeChild(playbackRatesButton)

      player.controlBar.addChild('CustomPlaybackRateButton', {}, index)
    }
  })
})
